import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { UploadStaStakeHolders } from '@efex-services/client/types';
import clientSvc from '@efex-services/client/clientSvc';
import { selectBusiness } from '@pages/profile/store/selectors';
import { aesEncryptCBC } from '@utils/crypt';
import { MexicoStakeHoldersFormData } from '@pages/onboarding/Mexico/types/stakeholders.type';
import documentsSvc from '@efex-services/documents/documentsSvc';
import useNewStakeholderService from './useNewStakeholderService';
import {
  UseHandleStakeholderRequestConfig,
  UseHandleStakeholderRequestResponse,
} from './useHandleStakeholderRequest.types';

const useHandleStakeholderRequest = ({
  country,
  existingStakeholderId,
  setIsLoading,
  deleteDocumensToDelete,
  getAllCurrentDocumentsIds,
  govermentIdDocuments,
}: UseHandleStakeholderRequestConfig): UseHandleStakeholderRequestResponse => {
  const company = useSelector(selectBusiness);
  const { generateStakeholderPayload, metadata } =
    useNewStakeholderService(country);

  const executeRequest = async (
    values: UploadStaStakeHolders | MexicoStakeHoldersFormData,
    documentIds?: number[]
  ) => {
    const payload = generateStakeholderPayload(
      values as unknown as UploadStaStakeHolders,
      []
    );

    payload.documentIds = documentIds ?? getAllCurrentDocumentsIds();

    if (payload.ssn) {
      payload.ssn = aesEncryptCBC(payload.ssn);
    }

    let stakeholderResponse;

    try {
      if (existingStakeholderId) {
        await deleteDocumensToDelete();

        stakeholderResponse = await clientSvc.updateStakeHolder(
          {
            id: existingStakeholderId,
            ...payload,
          } as unknown as UploadStaStakeHolders,
          company?.enterpriseId ?? ''
        );
      } else {
        stakeholderResponse = await clientSvc.createStakeHolderV2({
          values: payload,
          enterpriseID: company?.enterpriseId ?? '',
        });
      }
    } finally {
      setIsLoading?.(false);
    }

    return stakeholderResponse;
  };

  useEffect(() => {
    if (metadata) {
      const documentsIds = Object.values(govermentIdDocuments).map((document) =>
        document ? document.id : undefined
      );

      documentsIds.forEach((documentId) => {
        if (!documentId) return;

        documentsSvc.asignStakeholderDocuments({
          metadata: metadata as Record<string, string>,
          enterpriseId: company?.enterpriseId as unknown as number,
          documentId: documentId as unknown as number,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata]);

  return { executeRequest };
};

export default useHandleStakeholderRequest;
